import { NgClass, NgTemplateOutlet } from "@angular/common";
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    HostListener,
    Input,
    OnDestroy,
    OnInit,
    ViewChild,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { TypeManagerDecorator } from "../../../../../main/type.map.service";
import { DurationType, FormulaI } from "../../../../../pojo/RateScheduleI";
import { S25Util } from "../../../../../util/s25-util";
import { S25IconComponent } from "../../../../s25-icon/s25.icon.component";
import { S25RadioModule } from "../../../../s25-radio/s25.radio.module";
import { S25TimepickerComponent } from "../../../../s25-timepicker/s25.timepicker.component";
import { Debounce } from "../../../../../decorators/debounce.decorator";
import { DurationI } from "../../../../../pojo/DurationI";
import { S25TimepickerModule } from "../../../../s25-timepicker/s25.timepicker.module";

@TypeManagerDecorator("s25-ng-formula-builder")
@Component({
    selector: "s25-ng-formula-builder",
    template: `@if (init) {
        <div class="formulaList" [class.pivoted]="isPivoted">
            <h3 class="c-margin-bottom--quarter ngBold" [class.pivoted]="isPivoted">Rate Formulas</h3>
            @if (formulas.length === 0) {
                <p class="no-formulas-msg">No Rate Formulas Added</p>
            }
            @for (formula of formulas; track formula; let i = $index) {
                @if (formula.status !== "del") {
                    <div class="formula-entry c-margin-bottom--quarter" [class.pivoted]="isPivoted">
                        <div class="thread-top" [ngClass]="{ 'not-first': i > 0 }"></div>
                        <div class="thread-bottom"></div>
                        <fieldset [class.pivoted]="isPivoted">
                            <legend>Time Range</legend>
                            <s25-ng-radio
                                class="c-margin-right--half"
                                [name]="i + '-' + radioNameHash"
                                [(value)]="formula.rangeType"
                                [radioValue]="'none'"
                                (valueChange)="handleTypeChange(formula, 'none')"
                                >None</s25-ng-radio
                            >
                            <s25-ng-radio
                                class="c-margin-right--half"
                                [name]="i + '-' + radioNameHash"
                                [(value)]="formula.rangeType"
                                [radioValue]="'time'"
                                (valueChange)="handleTypeChange(formula, 'time')"
                                >Specific Times</s25-ng-radio
                            >
                            <s25-ng-radio
                                class="c-margin-right--half"
                                [name]="i + '-' + radioNameHash"
                                [(value)]="formula.rangeType"
                                [radioValue]="'duration'"
                                (valueChange)="handleTypeChange(formula, 'duration')"
                                >Specific Duration</s25-ng-radio
                            >
                            @if (errorMsgMap[i]?.timeErrorMsg) {
                                <div class="error-message">
                                    {{ errorMsgMap[i].timeErrorMsg }}
                                </div>
                            }
                            @if (formula.rangeType === "time") {
                                <div class="time-range c-margin-top--half">
                                    <label>
                                        Start Time
                                        <s25-timepicker
                                            [(modelValue)]="formula.durStart"
                                            (modelValueChange)="normalizeTime(formula)"
                                        ></s25-timepicker>
                                    </label>
                                    <label>
                                        End Time
                                        <s25-timepicker
                                            [(modelValue)]="formula.durEnd"
                                            (modelValueChange)="normalizeTime(formula)"
                                        ></s25-timepicker>
                                    </label>
                                </div>
                            }
                            @if (formula.rangeType === "duration") {
                                @let durationStart = getTimeAsDuration(formula, "Start");
                                @let durationEnd = getTimeAsDuration(formula, "End");
                                <div class="c-margin-top--half">
                                    <div>Minimum:</div>
                                    <ng-container
                                        *ngTemplateOutlet="
                                            durationInputs;
                                            context: { duration: durationStart, formula: formula }
                                        "
                                    ></ng-container>
                                    <div class="c-margin-top--quarter">Maximum:</div>
                                    <ng-container
                                        *ngTemplateOutlet="
                                            durationInputs;
                                            context: { duration: durationEnd, formula: formula }
                                        "
                                    ></ng-container>
                                </div>
                            }
                        </fieldset>
                        <fieldset class="formula-wrapper" [class.pivoted]="isPivoted">
                            <legend
                                [ngClass]="{
                                    'error-true': errorMsgMap[i]?.formulaErrorMsg,
                                    'c-margin-top--half': isPivoted,
                                }"
                            >
                                Formula
                            </legend>
                            @if (errorMsgMap[i]?.formulaErrorMsg) {
                                <div class="error-message">
                                    {{ errorMsgMap[i].formulaErrorMsg }}
                                </div>
                            }
                            <textarea
                                [(ngModel)]="formula.expression"
                                (ngModelChange)="setStatus(formula)"
                                aria-label="Rate Formula"
                                class="cn-form__control"
                                [ngClass]="{ 'large-area': formula.rangeType === 'duration' }"
                            ></textarea>
                        </fieldset>
                        <div class="button-group" [class.pivoted]="isPivoted">
                            @if (formulas.length > 1) {
                                <button class="aw-button aw-button--danger--outline" (click)="delete(formula)">
                                    Remove
                                </button>
                            }
                        </div>
                    </div>
                    @if ($last) {
                        <button class="c-textButton" (click)="save(formula, i)">
                            <s25-ng-icon [type]="'plus'" [ariaLabel]="false" />
                            Add Formula
                        </button>
                    }
                }
            }
        </div>

        <!-- Duration form -->
        <ng-template #durationInputs let-duration="duration" let-formula="formula">
            <label class="c-margin-left--half">
                Days
                <input
                    class="c-input c-margin-right--half"
                    type="number"
                    min="0"
                    max="65536"
                    [(ngModel)]="duration.days"
                    (ngModelChange)="setStatus(formula)"
                />
            </label>
            <label>
                Hours
                <input
                    class="c-input c-margin-right--half"
                    type="number"
                    min="0"
                    max="23"
                    [(ngModel)]="duration.hours"
                    (ngModelChange)="setStatus(formula)"
                />
            </label>
            <label>
                Minutes
                <input
                    class="c-input c-margin-right--half"
                    type="number"
                    min="0"
                    max="59"
                    [(ngModel)]="duration.minutes"
                    (ngModelChange)="setStatus(formula)"
                />
            </label>
        </ng-template>
    }`,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [S25RadioModule, FormsModule, NgClass, S25TimepickerModule, S25IconComponent, NgTemplateOutlet],
})
export class S25FormulaBuilderComponent implements OnInit, OnDestroy {
    @Input() formulas: FormulaI[];
    @Input() isEdit: boolean = false;
    @Input() sheetIndex: number;
    @Input() validateFunc: (formula: FormulaI, index: number) => { isValid: boolean; errorMap: FormulaErrorObj };

    @ViewChild(S25TimepickerComponent) timepickerComp: S25TimepickerComponent;

    init: boolean;
    maxBreakPoint: number = 0;
    errorMsgMap: { [key: number]: { [key: string]: string } } = {};
    isPivoted: boolean;
    radioNameHash: number;

    constructor(private cd: ChangeDetectorRef) {}

    ngOnInit() {
        this.radioNameHash = Math.floor(Math.random() * 1000);

        this.errorMsgMap[0] = { formulaErrorMsg: "", timeErrorMsg: "" };

        this.formulas.forEach((formula) => {
            this.maxBreakPoint = Math.max(this.maxBreakPoint, parseInt(formula.break_point as string));
            formula.date = { date: formula.startDt ?? formula.effectiveDate };

            this.setRangeDisplay(formula);
        });

        this.isPivoted = window.innerWidth <= 1290;

        this.init = true;
        this.cd.markForCheck();
    }

    @HostListener("window:resize")
    @Debounce(100)
    onWindowResize() {
        this.isPivoted = window.innerWidth <= 1290;
        this.cd.markForCheck();
    }

    setRangeDisplay(formula: FormulaI) {
        if (formula.durStart === formula.durEnd) formula.rangeType = "none";

        if (formula.rangeType === "time") {
            formula.startTime = S25Util.date.toTimeStr(formula.durStart);
            formula.endTime = S25Util.date.toTimeStr(formula.durEnd);
        }
    }

    handleTypeChange(formula: FormulaI, rangeType: FormulaI["rangeType"]) {
        formula.rangeType = rangeType;

        if (rangeType === "none") {
            formula.durStart = formula.durEnd = "";
        } else if (rangeType === "time") {
            formula.durStart = formula.durEnd = S25Util.date.parse("00:00");
        } else if (rangeType === "duration") {
            formula.durStart = { days: 0, hours: 0, minutes: 0 };
            formula.durEnd = { days: 0, hours: 0, minutes: 0 };
        }
        this.setStatus(formula);
        this.cd.markForCheck();
    }

    addFormula() {
        this.formulas.push({
            durStart: "",
            durEnd: "",
            rangeType: "none",
            expression: "",
            status: "new",
            break_point: this.maxBreakPoint + 1,
        });

        this.maxBreakPoint += 1;
    }

    setErrorMsg(errorObj: FormulaErrorObj, index: number) {
        this.errorMsgMap[index] = errorObj;
        this.cd.markForCheck();
    }

    save(formula: FormulaI, index: number) {
        const { isValid, errorMap } = this.validateFunc(formula, index);

        if (isValid) {
            this.setRangeDisplay(formula);
            this.errorMsgMap[index] = { timeErrorMsg: "", formulaErrorMsg: "" };
            this.addFormula();
            this.cd.markForCheck();
            return;
        }

        this.setErrorMsg(errorMap, index);
    }

    delete(formula: FormulaI) {
        if (formula.status === "new") {
            S25Util.array.inplaceRemoveByProp(this.formulas, "break_point", formula.break_point);
        } else {
            formula.status = "del";
        }

        this.errorMsgMap = {};
        this.cd.markForCheck();
    }

    setStatus(formula: FormulaI) {
        if (this.isEdit) formula.status = "mod";
    }

    normalizeTime(formula: FormulaI) {
        formula.durStart = S25Util.date.toTimeStr(formula.durStart); //S25Util.date.parse(formula.durStart);
        formula.durEnd = S25Util.date.toTimeStr(formula.durEnd);
        this.setStatus(formula);
    }

    ngOnDestroy() {
        this.errorMsgMap = {};
        this.cd.markForCheck();
    }

    isDuration(value: DurationType): value is DurationI {
        return typeof value === "object" && value !== null && !(value instanceof Date);
    }

    getTimeAsDuration(formula: FormulaI, type: "Start" | "End"): DurationI {
        const prop = formula[`dur${type}`];
        return this.isDuration(prop) ? prop : { days: 0, hours: 0, minutes: 0 };
    }
}

export type FormulaErrorObj = {
    timeErrorMsg: string;
    formulaErrorMsg: string;
};
